import { useCallback, useEffect, useRef, useState } from 'react';

/** Containers the API accepts, best first; Safari records mp4. */
const MIME_CANDIDATES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];

export type MicStatus = 'idle' | 'asking' | 'ready' | 'denied' | 'unsupported';

export interface Recording {
  blob: Blob;
  durationSec: number;
}

export interface SpeakingRecorder {
  micStatus: MicStatus;
  /** Voice level 0–1, sampled about 15 times a second while the mic is open. */
  level: number;
  recording: boolean;
  /** Seconds since the current recording started. */
  elapsed: number;
  requestMic: () => Promise<boolean>;
  start: () => boolean;
  /** Stops the current recording; resolves with the audio (null when nothing was recording). */
  stop: () => Promise<Recording | null>;
}

function pickMime(): string | undefined {
  if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') return undefined;
  return MIME_CANDIDATES.find((m) => MediaRecorder.isTypeSupported(m));
}

/**
 * Microphone, level meter and MediaRecorder for the Speaking test. Everything
 * it opens (tracks, audio context, timers) is closed on unmount.
 */
export function useSpeakingRecorder(onInterrupted?: (recording: Recording) => void): SpeakingRecorder {
  const [micStatus, setMicStatus] = useState<MicStatus>('idle');
  const [level, setLevel] = useState(0);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  const streamRef = useRef<MediaStream | null>(null);
  const contextRef = useRef<AudioContext | null>(null);
  const frameRef = useRef<number | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef(0);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const onInterruptedRef = useRef(onInterrupted);
  onInterruptedRef.current = onInterrupted;

  const meter = useCallback((stream: MediaStream) => {
    const Ctx: typeof AudioContext | undefined = window.AudioContext ?? (window as any).webkitAudioContext;
    if (!Ctx) return;
    const context = new Ctx();
    const analyser = context.createAnalyser();
    analyser.fftSize = 512;
    context.createMediaStreamSource(stream).connect(analyser);
    contextRef.current = context;
    const data = new Uint8Array(analyser.fftSize);
    let last = 0;
    const loop = (t: number) => {
      frameRef.current = requestAnimationFrame(loop);
      if (t - last < 66) return;
      last = t;
      analyser.getByteTimeDomainData(data);
      let sum = 0;
      for (const v of data) sum += ((v - 128) / 128) ** 2;
      setLevel(Math.min(1, Math.sqrt(sum / data.length) * 4));
    };
    frameRef.current = requestAnimationFrame(loop);
  }, []);

  const requestMic = useCallback(async () => {
    if (streamRef.current) return true;
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setMicStatus('unsupported');
      return false;
    }
    setMicStatus('asking');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      streamRef.current = stream;
      meter(stream);
      setMicStatus('ready');
      return true;
    } catch {
      setMicStatus('denied');
      return false;
    }
  }, [meter]);

  const clearTick = () => {
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = null;
  };

  const start = useCallback(() => {
    const stream = streamRef.current;
    if (!stream || recorderRef.current) return false;
    const mimeType = pickMime();
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    chunksRef.current = [];
    recorder.ondataavailable = (e) => {
      if (e.data.size) chunksRef.current.push(e.data);
    };
    recorder.start(1000);
    recorderRef.current = recorder;
    startedAtRef.current = performance.now();
    setElapsed(0);
    setRecording(true);
    tickRef.current = setInterval(() => setElapsed((performance.now() - startedAtRef.current) / 1000), 200);
    return true;
  }, []);

  const stop = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder) return Promise.resolve(null);
    recorderRef.current = null;
    clearTick();
    setRecording(false);
    const durationSec = (performance.now() - startedAtRef.current) / 1000;
    return new Promise<Recording | null>((resolve) => {
      recorder.onstop = () => {
        const type = recorder.mimeType || chunksRef.current[0]?.type || 'audio/webm';
        resolve({ blob: new Blob(chunksRef.current, { type }), durationSec });
      };
      if (recorder.state === 'inactive') recorder.onstop(new Event('stop'));
      else recorder.stop();
    });
  }, []);

  useEffect(
    () => () => {
      clearTick();
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      const recorder = recorderRef.current;
      if (recorder && recorder.state !== 'inactive') {
        // Unmounted mid-answer (the section clock ran out): hand the answer over instead of dropping it.
        const durationSec = (performance.now() - startedAtRef.current) / 1000;
        recorder.onstop = () => {
          const type = recorder.mimeType || chunksRef.current[0]?.type || 'audio/webm';
          onInterruptedRef.current?.({ blob: new Blob(chunksRef.current, { type }), durationSec });
        };
        recorder.stop();
      }
      streamRef.current?.getTracks().forEach((track) => track.stop());
      contextRef.current?.close().catch(() => undefined);
    },
    [],
  );

  return { micStatus, level, recording, elapsed, requestMic, start, stop };
}
